import React, { useState, useEffect } from 'react';
import { DollarSign, CreditCard, Clock, CheckCircle, XCircle, Eye, Search, ChevronLeft, ChevronRight, Menu, X, AlertTriangle, Globe, Mail, GamepadIcon } from 'lucide-react';
import { socket } from '../Containers/MainContainer';
import { Link, useParams } from 'react-router-dom';
import { numberWithCommas } from '../utils';

const PlutoWithdrawal = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const { referral_id, page2, second_page, third_page } = useParams();

  useEffect(() => {
    fetchWithdrawals();
  }, [currentPage, searchTerm, statusFilter]);

  const fetchWithdrawals = () => {
    setLoading(true);
    socket.emit('getWithdrawalsAdmin', {
      page: currentPage,
      search: searchTerm,
      status: statusFilter
    });

    socket.on('response_get_admin_withdrawals', (data) => {
      setWithdrawals(data.withdrawals || []);
      setStats({
        total_paid: data.stats.total_paid,
        total_requests: data.stats.total_requests,
        total_requests_cash: data.stats.total_requests_cash,
        processing_count:data.stats.processing_count,
        review_count:data.stats.review_count,
        urgent_pending:data.stats.urgent_pending,
        pending_count: data.stats.pending_count || 0,
        approved_count: data.stats.approved_count || 0,
        rejected_count: data.stats.rejected_count || 0
      });
      setPagination({
        currentPage: currentPage,
        totalPages: data.pagination.totalPages,
        totalWithdrawals: data.withdrawalsLength || 0,
        withdrawalsPerPage: 10
      });
      setLoading(false);
    });
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  // Check for suspicious user activity
  const getSuspiciousFlags = (withdrawal) => {
    const flags = [];
    
    // Multiple countries check
    if ((withdrawal.flag_reason+'').includes('Multiple countries detected') || (withdrawal.flag_reason+'').includes('Country mismatch')) {
      flags.push({
        type: 'multiple_countries',
        icon: <Globe className="h-3 w-3" />,
        text: 'Multiple Countries',
        color: 'text-orange-400',
        tooltip: `Used multiple countries`
      });
    }
    
    // Temp email check
    const tempEmailDomains = ['tempmail', '10minutemail', 'guerrillamail', 'mailinator', 'yopmail', 'throwaway'];
    if ((withdrawal.flag_reason+'').includes('email pattern')) {
      flags.push({
        type: 'temp_email',
        icon: <Mail className="h-3 w-3" />,
        text: 'Temp Email',
        color: 'text-red-400',
        tooltip: `Temporary email: ${withdrawal.email}`
      });
    }
    
    // US playtime check (if user is not from US but has US playtime)
    if ((withdrawal.flag_reason+'').toLowerCase().includes('play time')) {
      flags.push({
        type: 'us_playtime',
        icon: <GamepadIcon className="h-3 w-3" />,
        text: 'US Play',
        color: 'text-yellow-400',
        tooltip: `${withdrawal.us_playtime} minutes US playtime from ${withdrawal.user_country}`
      });
    }
    
    return flags;
  };

  const getStatusBadge = (withdrawal) => {
    const { status, verified_auto_pay, status_text } = withdrawal;
    
    const statusMap = {
      'paid': { color: 'green', text: 'Paid', icon: '✓' },
      'pending': { color: 'yellow', text: 'Pending Review', icon: '⏳' },
      'processing': { 
        color: verified_auto_pay ? 'blue' : 'purple', 
        text: verified_auto_pay ? 'Auto Processing' : 'Manual Processing',
        icon: verified_auto_pay ? '🤖' : '👤'
      },
      'review': { color: 'orange', text: 'In Review', icon: '👁️' }
    };

    const statusInfo = statusMap[status] || { color: 'gray', text: status_text || 'Unknown', icon: '?' };

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        statusInfo.color === 'green' ? 'bg-green-100 text-green-800' :
        statusInfo.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
        statusInfo.color === 'blue' ? 'bg-blue-100 text-blue-800' :
        statusInfo.color === 'purple' ? 'bg-purple-100 text-purple-800' :
        statusInfo.color === 'orange' ? 'bg-orange-100 text-orange-800' :
        'bg-gray-100 text-gray-800'
      }`}>
        <span className="mr-1">{statusInfo.icon}</span>
        <span className="hidden sm:inline">{statusInfo.text}</span>
        <span className="sm:hidden">{statusInfo.icon}</span>
      </span>
    );
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color = "blue" }) => (
    <div className="bg-gray-800 rounded-lg p-2 min-w-[120px] flex-shrink-0">
      <div className="flex items-center gap-2">
        <div className={`p-1 rounded`}>
          <Icon className={`h-4 w-4 text-${color}-400`} />
        </div>
        <div>
          <div className="text-sm font-bold text-gray-200">
            {typeof value === 'number' ? numberWithCommas(value) : value || '0'}
          </div>
          <div className="text-xs text-gray-400">{title}</div>
        </div>
      </div>
    </div>
  );

  const PaginationButton = ({ page, active, onClick, disabled, children }) => (
    <button
      onClick={() => onClick(page)}
      disabled={disabled}
      className={`px-2 py-1.5 text-sm rounded transition-colors ${
        active
          ? 'bg-blue-600 text-white'
          : disabled
          ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
      }`}
    >
      {children || page}
    </button>
  );

  const MobileWithdrawalCard = ({ withdrawal }) => {
    const suspiciousFlags = getSuspiciousFlags(withdrawal);
    
    return (
      <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
        {/* Header with ID and Status */}
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-sm font-medium text-gray-300">#{withdrawal.id}</div>
            <div className="text-xs text-gray-500">
              ({withdrawal.user_id}) {withdrawal.username || 'Unknown'}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            {getStatusBadge(withdrawal)}
            {withdrawal.verified_auto_pay && (
              <div className="text-xs text-blue-400">🤖</div>
            )}
          </div>
        </div>

        {/* Suspicious Activity Warnings */}
        {suspiciousFlags.length > 0 && (
          <div className="mb-2 p-2 bg-red-900/20 border border-red-800/30 rounded">
            <div className="flex items-center gap-1 mb-1">
              <AlertTriangle className="h-3 w-3 text-red-400" />
              <span className="text-xs text-red-400 font-medium">Suspicious Activity</span>
            </div>
            <div className="flex flex-wrap gap-1">
              {suspiciousFlags.map((flag, index) => (
                <div 
                  key={index}
                  className={`flex items-center gap-1 px-1.5 py-0.5 bg-gray-800/50 rounded text-xs ${flag.color}`}
                  title={flag.tooltip}
                >
                  {flag.icon}
                  <span>{flag.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Compact Info Grid */}
        <div className="grid grid-cols-2 gap-2 mb-2 text-xs">
          <div>
            <span className="text-gray-400">Amount:</span>
            <span className="text-gray-200 font-medium ml-1">{withdrawal.amount || '0'}</span>
          </div>
          <div>
            <span className="text-gray-400">Type:</span>
            <span className="text-gray-200 ml-1">{withdrawal.type || 'N/A'}</span>
          </div>
          <div>
            <span className="text-gray-400">Date:</span>
            <span className="text-gray-200 ml-1">{withdrawal.created_date}</span>
          </div>
          <div>
            <span className="text-gray-400">Method:</span>
            <span className="text-gray-200 ml-1 truncate">{withdrawal.method || 'N/A'}</span>
          </div>
        </div>
        
        {/* Days Pending Warning */}
        {withdrawal.days_pending !== null && withdrawal.days_pending > 0 && (
          <div className={`text-xs mb-2 ${withdrawal.days_pending > 2 ? 'text-red-400' : 'text-yellow-400'}`}>
            ⏰ {withdrawal.days_pending} days pending
          </div>
        )}
        
        {/* Flag Reason */}
        {withdrawal.flag_reason && (
          <div className="text-xs text-orange-400 mb-2" title={withdrawal.flag_reason}>
            ⚠️ {withdrawal.flag_reason.length > 30 ? withdrawal.flag_reason.substring(0, 30) + '...' : withdrawal.flag_reason}
          </div>
        )}
        
        {/* Action Button */}
        <div className="flex justify-end">
          <Link 
            to={`/pluto/withdrawals/${withdrawal.id}`} 
            className="flex items-center gap-1 text-blue-400 hover:text-blue-300 transition-colors text-xs font-medium"
          >
            <Eye className="h-3 w-3" />
            View
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white ">
      {/* Mobile Header - Fixed and Compact */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-gray-900 border-b border-gray-800 z-40">
        <div className="px-3 py-2">
          <h1 className="text-lg font-bold text-gray-200">Withdrawals</h1>
        </div>
      </div>

      {/* Mobile Content Spacer */}

      <div className="max-w-7xl mx-auto p-2 lg:p-6">
        {/* Desktop Header */}
        <div className="hidden lg:block mb-6">
          <h1 className="text-3xl font-bold text-gray-300">Withdrawal Management</h1>
          <p className="mt-2 text-gray-200">Monitor and manage withdrawal requests</p>
        </div>

        {/* Compact Stats Grid */}
        <div className="flex overflow-x-auto gap-2 mb-3 pb-1">
          <StatCard icon={DollarSign} title="Paid" value={`$${stats.total_paid || '0'}`} color="green" />
          <StatCard icon={CreditCard} title="Requests" value={`$${stats.total_requests_cash || '0'}`} color="blue" />
          <StatCard icon={Clock} title="Pending" value={stats.pending_count || 0} color="yellow" />
          <StatCard icon={Eye} title="Processing" value={stats.processing_count || 0} color="purple" />
          <StatCard icon={CheckCircle} title="Review" value={stats.review_count || 0} color="orange" />
          <StatCard icon={XCircle} title="Urgent" value={stats.urgent_pending || 0} color="red" />
        </div>
          <div className={`${showMobileFilters ? 'grid' : 'grid'} flex grid-cols-2 flex-row gap-2 mb-3`}>
            <button
              onClick={ ()=>{ 
                socket.emit('adminAction', {
                    action: 'recheck_withdrawals',
                  });
                socket.emit('getWithdrawalsAdmin', {
      page: currentPage,
      search: searchTerm,
      status: statusFilter
    });}}
              className={`px-3 py-2 rounded text-sm transition-colors bg-gray-800 text-gray-300 hover:bg-gray-700`}
            >
              <span className="">Recheck All</span>
            </button>
             <button
              onClick={()=>{socket.emit('adminAction', {
                    action: 'release_withdrawals',
                  });
                socket.emit('getWithdrawalsAdmin', {
      page: currentPage,
      search: searchTerm,
      status: statusFilter
    });}}
              className={`px-3 py-2 rounded text-sm transition-colors bg-gray-800 text-gray-300 hover:bg-gray-700`}
            >
              <span className="">Release All</span>
            </button>
            </div>
        {/* Compact Mobile Filters */}
        <div className="lg:hidden mb-3">
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="w-full flex items-center justify-between px-3 py-2 bg-gray-800 rounded text-sm"
          >
            <span>
              {statusFilter === 'pending' ? '⏳ Pending' : 
               statusFilter === 'all' ? 'All' :
               statusFilter === 'processing' ? '⚡ Processing' :
               statusFilter === 'review' ? '👁️ Review' :
               statusFilter === 'paid' ? '✅ Paid' :
               statusFilter === 'auto_verify' ? '🤖 Auto' : 'Filter'}
            </span>
            {showMobileFilters ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>

        {/* Filter Buttons */}
        <div className={`${showMobileFilters ? 'grid' : 'hidden'} lg:flex grid-cols-2 lg:flex-row gap-2 mb-3`}>
          {[
            { key: 'pending', label: '⏳ Pending', icon: '⏳' },
            { key: 'all', label: 'All', icon: '📋' },
            { key: 'processing', label: '⚡ Processing', icon: '⚡' },
            { key: 'review', label: '👁️ Review', icon: '👁️' },
            { key: 'paid', label: '✅ Paid', icon: '✅' },
            { key: 'auto_verify', label: '🤖 Auto', icon: '🤖' }
          ].map(filter => (
            <button
              key={filter.key}
              onClick={() => handleStatusFilter(filter.key)}
              className={`px-3 py-2 rounded text-sm transition-colors ${
                statusFilter === filter.key
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
            >
              <span className="lg:hidden">{filter.icon}</span>
              <span className="hidden lg:inline">{filter.label}</span>
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div className="bg-gray-800 rounded-lg border border-gray-700">
          {/* Compact Search */}
          <div className="p-3 border-b border-gray-700">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by ID or amount..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-8 pr-3 py-2 w-full bg-gray-900 text-gray-200 rounded text-sm focus:ring-2 focus:ring-blue-500 border-0"
              />
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">User</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {loading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-4 py-3"><div className="h-4 bg-gray-700 rounded w-12"></div></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-700 rounded w-24"></div></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-700 rounded w-16"></div></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-700 rounded w-20"></div></td>
                      <td className="px-4 py-3"><div className="h-4 bg-gray-700 rounded w-24"></div></td>
                      <td className="px-4 py-3"><div className="h-6 bg-gray-700 rounded w-16"></div></td>
                      <td className="px-4 py-3"><div className="h-6 bg-gray-700 rounded w-12"></div></td>
                    </tr>
                  ))
                ) : (
                  withdrawals.map((withdrawal) => {
                    const suspiciousFlags = getSuspiciousFlags(withdrawal);
                    return (
                      <tr key={withdrawal.id} className="hover:bg-gray-700/30 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-300">#{withdrawal.id}</td>
                        <td className="px-4 py-3">
                          <div>
                            <div className="text-sm text-gray-300">
                              ({withdrawal.user_id}) {withdrawal.username || 'Unknown'}
                            </div>
                            {suspiciousFlags.length > 0 && (
                              <div className="flex gap-1 mt-1">
                                {suspiciousFlags.map((flag, index) => (
                                  <div key={index} className={`flex items-center gap-1 text-xs ${flag.color}`} title={flag.tooltip}>
                                    {flag.icon}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-300">{withdrawal.type}</td>
                        <td className="px-4 py-3 text-sm text-gray-300">{withdrawal.amount}</td>
                        <td className="px-4 py-3 text-sm text-gray-300">{withdrawal.created_date}</td>
                        <td className="px-4 py-3">{getStatusBadge(withdrawal)}
                            {withdrawal.flag_reason && (
          <div className="text-xs text-orange-400 mb-2" title={withdrawal.flag_reason}>
            ⚠️ {withdrawal.flag_reason.length > 30 ? withdrawal.flag_reason.substring(0, 30) + '...' : withdrawal.flag_reason}
          </div>
        )}
                        </td>
                        <td className="px-4 py-3">
                          <Link to={`/pluto/withdrawals/${withdrawal.id}`} className="text-blue-400 hover:text-blue-300">
                            <Eye className="h-4 w-4" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden">
            {loading ? (
              <div className="p-3 space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="bg-gray-700 rounded-lg p-3 animate-pulse">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <div className="h-4 bg-gray-600 rounded w-16"></div>
                        <div className="h-5 bg-gray-600 rounded w-12"></div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="h-3 bg-gray-600 rounded"></div>
                        <div className="h-3 bg-gray-600 rounded"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-3 space-y-3">
                {withdrawals.map((withdrawal) => (
                  <MobileWithdrawalCard key={withdrawal.id} withdrawal={withdrawal} />
                ))}
                {withdrawals.length === 0 && (
                  <div className="text-center py-8 text-gray-500">No withdrawals found</div>
                )}
              </div>
            )}
          </div>

          {/* Compact Pagination */}
          {pagination.totalPages > 1 && (
            <div className="border-t border-gray-700 p-3 mr-10">
              <div className="flex items-center justify-between text-sm text-gray-400">
                <span>
                  {((currentPage - 1) * pagination.withdrawalsPerPage) + 1}-{Math.min(currentPage * pagination.withdrawalsPerPage, pagination.totalWithdrawals)} of {numberWithCommas(pagination.totalWithdrawals)}
                </span>
                <div className="flex items-center gap-1">
                  <PaginationButton
                    page={currentPage - 1}
                    onClick={handlePageChange}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </PaginationButton>
                  <span className="px-2 text-gray-300">{currentPage} / {numberWithCommas(pagination.totalPages)}</span>
                  <PaginationButton
                    page={currentPage + 1}
                    onClick={handlePageChange}
                    disabled={currentPage === pagination.totalPages}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </PaginationButton>
                </div>
              </div>
            </div>
          )}
          {/* Quick jump input */}
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-400">Go to:</span>
          <input
            type="number"
            min="1"
            max={pagination.totalPages}
            placeholder={currentPage}
            className="w-20 px-2 py-1 text-sm bg-gray-900 text-gray-200 border border-gray-700 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                const page = parseInt(e.target.value);
                if (page >= 1 && page <= pagination.totalPages) {
                  handlePageChange(page);
                  e.target.value = '';
                }
              }
            }}
          />
          <span className="text-sm text-gray-400">of {numberWithCommas(pagination.totalPages)}</span>
        </div>
        </div>
        
      </div>
    </div>
  );
};

export default PlutoWithdrawal;